/**
 * Secure Authentication Manager
 * Handles automatic login with biometric authentication
 */

import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { NativeBiometric } from '@capgo/capacitor-native-biometric';

const SECURE_STORAGE_KEY = 'secure_user_credentials';

/**
 * Save user credentials securely after successful login
 */
export const saveUserCredentials = async (username, password) => {
  try {
    console.log('🔐 Saving user credentials securely for:', username);
    
    if (Capacitor.isNativePlatform()) {
      // On mobile, use secure storage
      await Preferences.set({
        key: SECURE_STORAGE_KEY,
        value: JSON.stringify({
          username,
          password,
          savedAt: new Date().toISOString()
        })
      });
      console.log('✅ Credentials saved securely on mobile');
    } else {
      // On web, use localStorage as fallback (for testing)
      localStorage.setItem(SECURE_STORAGE_KEY, JSON.stringify({
        username,
        password,
        savedAt: new Date().toISOString()
      }));
      console.log('✅ Credentials saved in localStorage (web fallback)');
    }
  } catch (error) {
    console.error('❌ Failed to save credentials:', error);
    throw error;
  }
};

/**
 * Get saved credentials from secure storage
 */
export const getSavedCredentials = async () => {
  try {
    console.log('🔍 Checking for saved credentials...');
    
    let stored = null;
    
    if (Capacitor.isNativePlatform()) {
      // On mobile, use secure storage
      const result = await Preferences.get({ key: SECURE_STORAGE_KEY });
      stored = result.value;
    } else {
      // On web, use localStorage as fallback
      stored = localStorage.getItem(SECURE_STORAGE_KEY);
    }
    
    if (stored) {
      const credentials = JSON.parse(stored);
      console.log('✅ Found saved credentials for:', credentials.username);
      return credentials;
    } else {
      console.log('ℹ️ No saved credentials found');
      return null;
    }
  } catch (error) {
    console.error('❌ Failed to get saved credentials:', error);
    return null;
  }
};

/**
 * Clear saved credentials (on logout)
 */
export const clearSavedCredentials = async () => {
  try {
    console.log('🗑️ Clearing saved credentials...');
    
    if (Capacitor.isNativePlatform()) {
      // On mobile, remove from secure storage
      await Preferences.remove({ key: SECURE_STORAGE_KEY });
    } else {
      // On web, remove from localStorage
      localStorage.removeItem(SECURE_STORAGE_KEY);
      // Also clear WebAuthn credentials
      localStorage.removeItem('webauthn_credential_id');
    }
    
    console.log('✅ Credentials cleared successfully');
  } catch (error) {
    console.error('❌ Failed to clear credentials:', error);
    throw error;
  }
};

/**
 * Check if biometric authentication is available
 */
export const isBiometricAvailable = async () => {
  try {
    if (!Capacitor.isNativePlatform()) {
      console.log('🌐 Web platform - checking WebAuthn for Touch ID/Face ID...');
      
      // Check for WebAuthn (Touch ID/Face ID in browsers)
      if (window.PublicKeyCredential && navigator.credentials) {
        console.log('✅ WebAuthn available - biometric should work');
        return true; // WebAuthn available
      } else {
        console.log('⚠️ WebAuthn not available - but still allow biometric flow');
        return true; // Allow biometric flow anyway (will handle in performBiometricAuth)
      }
    }
    
    const result = await NativeBiometric.isAvailable();
    console.log('🔍 Native biometric availability check:', result);
    return result.isAvailable;
  } catch (error) {
    console.error('❌ Biometric availability check failed:', error);
    // Return true to allow biometric flow - let performBiometricAuth handle the actual test
    return true;
  }
};

// Flag to prevent concurrent biometric authentication attempts
let biometricInProgress = false;

/**
 * Perform biometric authentication
 */
export const performBiometricAuth = async (userInfo = {}) => {
  // Prevent concurrent calls
  if (biometricInProgress) {
    console.log('⚠️ Biometric authentication already in progress, skipping duplicate call');
    return false;
  }
  
  biometricInProgress = true;
  
  // Identidade real do usuário para o passkey (evita "user@example.com")
  const waName = String((userInfo && (userInfo.username || userInfo.email)) || 'usuario');
  const waDisplay = String((userInfo && (userInfo.displayName || userInfo.fullName || userInfo.full_name || userInfo.username)) || waName);
  
  try {
    console.log('👤 Performing biometric authentication... STACK TRACE:', new Error().stack);
    
    if (!Capacitor.isNativePlatform()) {
      console.log('🌐 Web platform detected - checking for WebAuthn API...');
      
      // Check for WebAuthn (Touch ID/Face ID in browsers)
      if (window.PublicKeyCredential && navigator.credentials) {
        try {
          console.log('🔐 WebAuthn available - requesting biometric authentication...');
          
          // Check if we have existing credentials (second+ time)
          const existingCredentials = localStorage.getItem('webauthn_credential_id');
          
          if (existingCredentials) {
            console.log('🔍 Found existing WebAuthn credentials - verifying identity...');
            
            // VERIFY existing credential (second+ time)
            const credential = await navigator.credentials.get({
              publicKey: {
                challenge: new Uint8Array(32),
                rpId: window.location.hostname,
                allowCredentials: [{
                  type: "public-key",
                  id: new Uint8Array(JSON.parse(existingCredentials)),
                  transports: ["internal"]
                }],
                userVerification: "required",
                timeout: 60000
              }
            });
            
            if (credential) {
              console.log('✅ WebAuthn identity verification successful');
              return true;
            } else {
              console.log('❌ WebAuthn identity verification failed');
              return false;
            }
          } else {
            console.log('🆕 No existing credentials - creating new WebAuthn credential...');
            
            // CREATE new credential (first time)
            const credential = await navigator.credentials.create({
              publicKey: {
                challenge: new Uint8Array(32),
                rp: {
                  name: "PM Home Remodeling",
                  id: window.location.hostname
                },
                user: {
                  id: new TextEncoder().encode(waName),
                  name: waName,
                  displayName: waDisplay
                },
                pubKeyCredParams: [{ alg: -7, type: "public-key" }],
                authenticatorSelection: {
                  authenticatorAttachment: "platform",
                  userVerification: "required"
                },
                timeout: 60000,
                attestation: "direct"
              }
            });
            
            if (credential) {
              // Save credential ID for future verification
              const credentialId = Array.from(new Uint8Array(credential.rawId));
              localStorage.setItem('webauthn_credential_id', JSON.stringify(credentialId));
              console.log('✅ WebAuthn credential created and saved');
              return true;
            } else {
              console.log('❌ WebAuthn credential creation failed');
              return false;
            }
          }
        } catch (webauthnError) {
          console.log('❌ WebAuthn error:', webauthnError.message);
          // Check if we have existing credentials - no fallback allowed for verification
          const savedCredentialId = localStorage.getItem('webauthn_credential_id');
          if (savedCredentialId) {
            console.log('❌ Biometric verification failed - no fallback allowed for existing credentials');
            return false;
          }
          // Only allow fallback for new credential creation (first time)
          return window.confirm('Touch ID não disponível. Confirma que quer ativar login automático?');
        }
      } else {
        console.log('⚠️ WebAuthn not available, using confirmation dialog');
        // Check if we have saved credentials - if so, require biometric
        const savedCredentialId = localStorage.getItem('webauthn_credential_id');
        if (savedCredentialId) {
          console.log('❌ Biometric authentication required but not available - denying access');
          return false;
        }
        // Only allow fallback for new setup (first time)
        return window.confirm('Biometria não disponível no navegador. Confirma que quer ativar login automático?');
      }
    }
    
    // Native platform (iOS/Android)
    const result = await NativeBiometric.verifyIdentity({
      reason: 'Autenticar com Face ID ou Touch ID para entrar no app',
      title: 'Autenticação Biométrica',
      subtitle: 'Use sua biometria para acessar',
      description: 'Confirme sua identidade para continuar'
    });
    
    console.log('✅ Native biometric authentication successful');
    return true;
  } catch (error) {
    console.error('❌ Biometric authentication failed:', error);
    return false;
  } finally {
    // Reset the flag when done
    biometricInProgress = false;
  }
};

/**
 * Attempt automatic login with saved credentials and biometric auth
 */
export const attemptAutoLogin = async (loginFunction) => {
  try {
    console.log('🚀 Checking for saved credentials...');
    
    // 1. Check for saved credentials
    const savedCredentials = await getSavedCredentials();
    if (savedCredentials) {
      console.log('✅ Found saved credentials - redirecting to login screen with biometric prompt');
      // If credentials exist, go to login screen instead of auto-login
      // The login screen will handle the biometric authentication
      return false;
    }
    
    console.log('ℹ️ No saved credentials - manual login required');
    return false;
    
  } catch (error) {
    console.error('❌ Auto login check failed:', error);
    return false;
  }
};

/**
 * Perform the actual login with saved credentials
 */
const performAutoLogin = async (credentials, loginFunction) => {
  try {
    console.log('🔓 Performing auto-login for:', credentials.username);
    
    // Call the login function with saved credentials
    const success = await loginFunction(credentials.username, credentials.password);
    
    if (success) {
      console.log('🎉 Auto-login successful!');
      return true;
    } else {
      console.log('❌ Auto-login failed - clearing invalid credentials');
      await clearSavedCredentials();
      return false;
    }
  } catch (error) {
    console.error('❌ Auto-login error:', error);
    // If auto-login fails, clear potentially corrupted credentials
    await clearSavedCredentials();
    return false;
  }
};

/**
 * Confirm logout with user
 */
export const confirmLogout = () => {
  return new Promise((resolve) => {
    if (Capacitor.isNativePlatform()) {
      // On mobile, use native dialog
      const confirmed = window.confirm('Tem certeza que deseja sair? Você precisará fazer login novamente na próxima vez.');
      resolve(confirmed);
    } else {
      // On web, use browser confirm
      const confirmed = window.confirm('Tem certeza que deseja sair? Você precisará fazer login novamente na próxima vez.');
      resolve(confirmed);
    }
  });
};

export default {
  saveUserCredentials,
  getSavedCredentials,
  clearSavedCredentials,
  isBiometricAvailable,
  performBiometricAuth,
  attemptAutoLogin,
  confirmLogout
};